import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

// Lobi'de "Kayitlar" — toplantiya girmeden video kayitlarini oda + tarih/saate gore listele,
// tikla -> presigned MP4 link al -> modal video oynatici. SummariesHistory ile ayni gorsel dil.
// recordingApi = { listRecordings, getRecordingLink }.
type Item = { id: number; room: string; size?: number; duration?: number; created: number; is_owner?: boolean };
type Props = {
  recordingApi?: any;
  t?: (k: string, fb?: string) => string;
  onLoadingChange?: (loading: boolean) => void;
};

const unwrap = (r: any) => (r && r.data && r.data.result !== undefined ? r.data.result : (r && r.data) || r || null);

function fmtDate(ts: number, locale?: string) {
  try {
    return new Date(ts * 1000).toLocaleString(locale || undefined, {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(ts); }
}

function fmtDuration(sec?: number) {
  const s = Number(sec || 0);
  if (!s || s < 0) return "";
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function fmtSize(bytes?: number) {
  const b = Number(bytes || 0);
  if (!b || b < 0) return "";
  const mb = b / (1024 * 1024);
  if (mb < 0.1) return `${(b / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
}

export default function RecordingsList({ recordingApi, t, onLoadingChange }: Props) {
  const tr = (k: string, fb: string) => (t ? t(k, fb) : fb);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const load = useCallback(() => {
    if (!recordingApi?.listRecordings) { onLoadingChange?.(false); return; }
    setLoading(true);
    onLoadingChange?.(true);
    Promise.resolve(recordingApi.listRecordings())
      .then((r: any) => {
        const d = unwrap(r);
        setItems(Array.isArray(d?.items) ? d.items : []);
      })
      .catch(() => setItems([]))
      .finally(() => {
        setLoading(false);
        onLoadingChange?.(false);
      });
  }, [recordingApi, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback((id: number) => {
    if (!recordingApi?.getRecordingLink) return;
    setOpenId(id); setVideoUrl(null); setDetailError(false); setDetailLoading(true);
    Promise.resolve(recordingApi.getRecordingLink(id))
      .then((r: any) => {
        const d = unwrap(r);
        const url = d && typeof d.url === "string" ? d.url : "";
        if (url) setVideoUrl(url);
        else setDetailError(true);
      })
      .catch(() => setDetailError(true))
      .finally(() => setDetailLoading(false));
  }, [recordingApi]);

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const it of items) {
      if (!map.has(it.room)) map.set(it.room, []);
      map.get(it.room)!.push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  if (!recordingApi?.listRecordings) return null;

  const openInNewTab = () => {
    if (!videoUrl) return;
    try { window.open(videoUrl, "_blank", "noopener,noreferrer"); } catch (_) {}
  };

  const detailModal = openId != null ? createPortal(
    <div className="rl-backdrop" onClick={() => setOpenId(null)} role="dialog" aria-modal="true">
      <div className="rl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rl-mhead">
          <div className="rl-mhead-l">
            <span className="rl-mhead-ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-3v10l-5-3v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
            </span>
            <div className="rl-mtitle">{tr("memeetRecordings", "Kayitlar")}</div>
          </div>
          <button className="rl-close" onClick={() => setOpenId(null)} aria-label="x">x</button>
        </div>
        <div className="rl-mbody">
          {detailLoading && <div className="rl-loading">{tr("memeetLoading", "Yukleniyor...")}</div>}
          {detailError && !detailLoading && <div className="rl-loading">{tr("memeetRecordFailed", "Kayit acilamadi")}</div>}
          {videoUrl && !detailLoading ? (
            <video className="rl-video" src={videoUrl} controls autoPlay playsInline />
          ) : null}
        </div>
        {videoUrl && !detailLoading ? (
          <div className="rl-mfoot">
            <button className="rl-open" onClick={openInNewTab}>{tr("memeetOpenInNewTab", "Open in new tab")}</button>
          </div>
        ) : null}
      </div>
      <style>{RL_MODAL_CSS}</style>
    </div>,
    document.body
  ) : null;

  return (
    <section className="rl-root">
      <div className="rl-head">
        <h2>
          <span className="rl-head-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-3v10l-5-3v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
          </span>
          {tr("memeetRecordings", "Kayitlar")}
          {items.length ? <span className="rl-count">{items.length}</span> : null}
        </h2>
        <button className="rl-refresh" onClick={load} title={tr("memeetRefresh", "Yenile")} aria-label={tr("memeetRefresh", "Yenile")}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>
      </div>
      {loading && !items.length ? (
        <div className="rl-empty">{tr("memeetLoading", "Yukleniyor...")}</div>
      ) : !items.length ? (
        <div className="rl-empty">
          <span className="rl-empty-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-3v10l-5-3v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
          </span>
          {tr("memeetNoRecordings", "Henuz kayit yok")}
        </div>
      ) : (
        <div className="rl-rooms">
          {grouped.map(([room, list]) => (
            <div className="rl-room" key={room}>
              <div className="rl-room-head">
                <span className="rl-room-ico" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-3v10l-5-3v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
                </span>
                <span className="rl-room-name">{room}</span>
                <span className="rl-room-count">{list.length}</span>
              </div>
              <ul className="rl-list">
                {list.map((it) => (
                  <li className="rl-li" key={it.id}>
                    <button className="rl-item" onClick={() => openDetail(it.id)}>
                      <span className="rl-item-main">
                        <span className="rl-item-date">
                          {fmtDate(it.created)}
                          {it.is_owner ? <span className="rl-owner-chip">{tr("memeetOwner", "Owner")}</span> : null}
                        </span>
                        <span className="rl-item-meta">
                          {fmtDuration(it.duration) ? <span>{fmtDuration(it.duration)}</span> : null}
                          {fmtSize(it.size) ? <span>{fmtSize(it.size)}</span> : null}
                        </span>
                      </span>
                      <span className="rl-item-chev" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {detailModal}
      <style>{RL_CSS}</style>
    </section>
  );
}

const RL_CSS = `
.rl-root { margin-top: 28px; }
.rl-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.rl-head h2 { display: inline-flex; align-items: center; gap: 9px; font-size: 16px; font-weight: 700; margin: 0; color: inherit; }
.rl-head-ico { display: inline-flex; opacity: .85; }
.rl-count { font-size: 11.5px; font-weight: 700; padding: 1px 8px; border-radius: 999px; background: rgba(255,72,72,0.16); color: #ff9a9a; border: 1px solid rgba(255,72,72,0.3); }
.rl-refresh { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: inherit; border-radius: 9px; width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; opacity: .8; transition: background .15s, transform .3s; }
.rl-refresh:hover { opacity: 1; background: rgba(255,255,255,0.12); }
.rl-refresh:active svg { transform: rotate(-180deg); transition: transform .4s; }
.rl-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; font-size: 13.5px; opacity: .6; padding: 30px 18px; line-height: 1.55; }
.rl-empty-ico { display: inline-flex; opacity: .5; }
.rl-rooms { display: flex; flex-direction: column; gap: 14px; }
.rl-room { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; padding: 12px; }
.rl-room-head { display: flex; align-items: center; gap: 8px; padding: 2px 4px 10px; }
.rl-room-ico { display: inline-flex; opacity: .7; }
.rl-room-name { font-size: 13px; font-weight: 700; opacity: .92; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rl-room-count { margin-left: auto; font-size: 11px; font-weight: 700; opacity: .6; padding: 1px 7px; border-radius: 999px; background: rgba(255,255,255,0.06); }
.rl-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.rl-li { position: relative; display: flex; align-items: stretch; gap: 6px; }
.rl-item { flex: 1; min-width: 0; text-align: left; display: flex; align-items: center; gap: 10px; padding: 11px 13px; border-radius: 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: inherit; cursor: pointer; transition: background .15s, border-color .15s; }
.rl-item:hover { background: rgba(255,72,72,0.10); border-color: rgba(255,72,72,0.45); }
.rl-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.rl-item-date { font-size: 12.5px; font-weight: 700; display: inline-flex; align-items: center; gap: 8px; }
.rl-owner-chip { font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: rgba(255,72,72,0.16); color: #ff9a9a; border: 1px solid rgba(255,72,72,0.3); }
.rl-item-meta { font-size: 12px; opacity: .65; display: inline-flex; gap: 10px; }
.rl-item-chev { display: inline-flex; opacity: .35; flex: 0 0 auto; }
.rl-item:hover .rl-item-chev { opacity: .7; }
`;

const RL_MODAL_CSS = `
.rl-backdrop { position: fixed; inset: 0; background: rgba(4,7,12,0.62); backdrop-filter: blur(3px); z-index: 2147483200; display: flex; align-items: center; justify-content: center; padding: 20px; }
.rl-modal { width: min(820px, 96vw); max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; background: #11161e; color: #eaf0f6; border: 1px solid rgba(255,255,255,0.12); border-radius: 18px; box-shadow: 0 24px 70px rgba(0,0,0,0.65); animation: rlIn .25s ease both; }
@keyframes rlIn { from { opacity: 0; transform: translateY(14px) scale(.98); } to { opacity: 1; transform: none; } }
.rl-mhead { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); background: linear-gradient(180deg, rgba(255,72,72,0.07), transparent); }
.rl-mhead-l { display: flex; align-items: center; gap: 11px; min-width: 0; }
.rl-mhead-ico { display: inline-flex; width: 34px; height: 34px; border-radius: 10px; align-items: center; justify-content: center; background: rgba(255,72,72,0.14); color: #ff9a9a; border: 1px solid rgba(255,72,72,0.28); flex: 0 0 auto; }
.rl-mtitle { font-size: 16px; font-weight: 700; word-break: break-word; }
.rl-close { background: none; border: none; color: #9aa6b2; font-size: 22px; line-height: 1; cursor: pointer; padding: 0 4px; }
.rl-close:hover { color: #fff; }
.rl-mbody { padding: 16px 20px; overflow-y: auto; }
.rl-loading { opacity: .6; font-size: 13.5px; padding: 20px 0; text-align: center; }
.rl-video { width: 100%; max-height: 68vh; border-radius: 12px; background: #000; display: block; }
.rl-mfoot { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.08); }
.rl-open { padding: 9px 18px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.2); background: transparent; color: #eaf0f6; cursor: pointer; font-size: 13.5px; }
.rl-open:hover { background: rgba(255,255,255,0.09); }
`;
